import React, { useState, type FC, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ACTIVITY_QUERY, IS_ACTIVITY_ACCEPTED_QUERY, UPDATE_EMERGENCY_CONTACTS_MUTATION } from '../queries';
import { Spinner } from 'components/Shared';
import './ActivityDetails.scss';
import { useTranslation } from 'react-i18next';
import { PageMetadata } from 'components/Shared/PageMetadata';
import { Button } from 'components/Shared/Inputs';
import { FullScreenMessage } from 'components/Shared/FullScreenMessage';
import { useLazyQueryWithCacheExpiry } from 'providers/Apollo/useLazyQueryWithCacheExpiry';
import { useQueryWithCacheExpiry } from 'providers/Apollo/useQueryWithCacheExpiry';
import Container from '@mui/material/Container';
import { EmergencyContactFormList } from '../components/EmergencyContactFormList/EmergencyContactFormList';
import { useMutationWithExpiration } from 'providers/Apollo/useMutationWithExpiration';
import { Card } from 'components/Shared/Card';
import { FormViewContainer } from 'components/Forms/components/FormViewContainer';
import { getClientFullName } from 'utils';
import { useSnackbar } from 'notistack';
import { useHistoryState } from 'hooks/useHistoryState';
import { type WorkFlowType } from 'components/CaseManagement/interfaces';
import { type AssessmentPage } from 'components/Forms/interfaces';
import { useAuth } from 'components/Auth';

interface EmergencyContactViewProps { }

export const EmergencyContactView: FC<EmergencyContactViewProps> = (_) => {  
    const { t, ready: translationsReady } = useTranslation(['emergencyContact', 'clients'], { useSuspense: false });

    const { activityId: activityIdString } = useParams<{activityId: string}>();
    const activityId = parseInt(activityIdString || '');
    const { enqueueSnackbar } = useSnackbar();
    const { effectiveProviderData: { providerId } = {} } = useAuth();

    const navigate = useNavigate();
    const [historyState] = useHistoryState<{ workflow?: WorkFlowType, pages?: AssessmentPage[] }>();

    const [fetchAttempted, setFetchAttempted] = useState(false);

    const [getActivityData, activityQuery] = useLazyQueryWithCacheExpiry(ACTIVITY_QUERY, {
        variables: { activityId },
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
        onCompleted: () => setFetchAttempted(true),
        expiryEvents: [
            'UPDATE_APPT_STATUS_MUTATION',
            'TERMINATE_UPDATE_STATUS_TODO_MUTATION',
            'SAVE_FIRST_APPT_DATE',
            'ACCEPT_REFERRAL',
            'UPDATE_EMERGENCY_CONTACTS_MUTATION'
        ],
    });
    
    const activityAcceptedQuery = useQueryWithCacheExpiry(IS_ACTIVITY_ACCEPTED_QUERY, {
        variables: { activityId, providerId: providerId! },
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
        onCompleted: (data) => {
            if (data?.isActivityAcceptedByProvider) {
                getActivityData();
            } else {
                navigate(-1);
            }
        }
    });

    const activity = activityQuery.data?.activity;
    const forms = activityQuery.data?.formsByActivity;
    const clientName = getClientFullName(activity?.client!);
    const { clientAddressDeclined } = activity?.client?.contactAddress ?? {};

    const [updateEmergencyContacts, updateEmergencyContactsMutation] = useMutationWithExpiration(UPDATE_EMERGENCY_CONTACTS_MUTATION, {
        onCompleted: (data) => {
            if (data?.updateClientEmergencyContacts?.includes("Error")) {
                enqueueSnackbar(`${t('error_updating_contact_info')}... ${data.updateClientEmergencyContacts}`, { variant: "error" });
            } else {
                getActivityData({ variables: { activityId }, fetchPolicy: 'network-only' });
                const { workflow, pages } = historyState ?? {};
                workflow === "FAA"
                ? (
                    clientAddressDeclined === null
                    ? navigate({ pathname: `/clients/${activityId}/forms/contact_address` }, { state: { workflow, pages }})
                    : navigate({ pathname: `/clients/${activityId}/forms/first_appointment_assessment` }, { state: { pageNumber: 1, pages }})
                )
                : ["CloseCase", "ClinicalRecords"].includes(workflow!) 
                ? navigate(-1)
                : navigate(0);
            }
        }
    });

    const loading = !translationsReady || activityAcceptedQuery.loading || activityQuery.loading;

    // const onSubmitSuccess = () => navigate({ pathname: `/clients/${activityId}/forms/first_appointment_assessment` }, { state: { pageNumber: 1 }});

    const renderPageContent = useCallback(() => {
        const readonly = activity?.client?.clientEmergencyContacts?.length! > 0;
        if (loading) {
            return <Spinner fillParent />
        } else {
            if (!!activity) {
                return (
                    <div className="activity_details_content_wrapper">
                        <FormViewContainer activityId={activityId} clientName={clientName}>
                            <Container maxWidth="md">
                                <Card className="emergency-contact-page-card" elevation={2}>
                                    <h2>{t('emergency_contact_info')}</h2>
                                    {readonly && <span>{t('please_review_emergency_contact_info')}</span>}
                                    <EmergencyContactFormList
                                        activity={activity}
                                        busy={updateEmergencyContactsMutation.loading || activityQuery.loading}
                                        onSubmit={updateEmergencyContacts}
                                        readonly={readonly}
                                    />
                                </Card>
                            </Container>
                        </FormViewContainer>
                    </div>
                );
            } else if (fetchAttempted && !activity) {
                return (
                    <FullScreenMessage title={t('activity_not_available_message')}>
                        <Button variant="outline" component={Link} to={{pathname: '/clients'}}>
                            {t('go_to_activities_list_button_label')}
                        </Button>
                    </FullScreenMessage>
                );
            }
        }
    }, [loading, activity, forms]);

    return (
        <>
            {
                translationsReady && 
                <PageMetadata 
                    pageTitle={t('emergency_contact_page_title')}
                    pageDescription={t('emergency_contact_page_description')}
                /> 
            }
            <div className="activity_details_view_container">
                {
                    renderPageContent()
                }
            </div>
        </>
    );
}
